import { useState, useEffect } from 'react';

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export interface DeviceState {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  device: DeviceType;
}

const getDeviceState = (mobileBp = 640, desktopBp = 1024): DeviceState => {
  if (typeof window === "undefined") {
    return { isMobile: false, isTablet: false, isDesktop: true, device: 'desktop' };
  }
  const width = window.innerWidth;
  const isMobile = width < mobileBp;
  const isTablet = width >= mobileBp && width < desktopBp;
  const isDesktop = width >= desktopBp;
  const device: DeviceType = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

  return { isMobile, isTablet, isDesktop, device };
};

/**
 * Universal Hook for 3 Breakpoints: Mobile, Tablet, Desktop
 * Default Breakpoints:
 * - Mobile: < 640px
 * - Tablet: >= 640px and < 1024px
 * - Desktop: >= 1024px
 */
export function useDevice(mobileBp = 640, desktopBp = 1024): DeviceState {
  const [deviceState, setDeviceState] = useState<DeviceState>(() =>
    getDeviceState(mobileBp, desktopBp)
  );

  useEffect(() => {
    const handleResize = () => {
      setDeviceState(getDeviceState(mobileBp, desktopBp));
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBp, desktopBp]);

  return deviceState;
}

/**
 * Single-purpose Mobile hook (default breakpoint = 768px)
 */
export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
}

/**
 * Single-purpose Tablet hook (640px to 1024px)
 */
export function useIsTablet(mobileBp = 640, desktopBp = 1024) {
  const { isTablet } = useDevice(mobileBp, desktopBp);
  return isTablet;
}

/**
 * Single-purpose Desktop hook (>= 1024px)
 */
export function useIsDesktop(desktopBp = 1024) {
  const { isDesktop } = useDevice(640, desktopBp);
  return isDesktop;
}
